import get from 'lodash/get';
import axiosConfig, { ndtApiBaseUrl } from '../../../redux/NDT Analytics/axiosConfig';

const NO_RECORDS = 'No records found for the provided filters.';

export type NdtFetchResult<T> = {
  data: T | null;
  noRecords: boolean;
  error: string | null;
};

function unwrap(res: { data?: unknown }): unknown {
  return get(res, ['data', 'data'], (res as { data?: unknown }).data);
}

function isNoRecords(body: unknown): boolean {
  return Boolean(
    body &&
      typeof body === 'object' &&
      (body as { detail?: string }).detail === NO_RECORDS,
  );
}

function readNdtError(error: unknown): string {
  const raw = readNdtErrorRaw(error);
  if (raw.includes('<!DOCTYPE') || raw.includes('<html') || raw.includes('Cannot POST')) {
    return 'NDT analytics could not be reached.';
  }
  return raw;
}

function readNdtErrorRaw(error: unknown): string {
  if (typeof error === 'string' && error.trim()) return error;
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === 'object') {
    const record = error as { detail?: unknown; message?: unknown };
    if (typeof record.detail === 'string' && record.detail.trim()) return record.detail;
    if (Array.isArray(record.detail)) {
      const text = record.detail
        .map((item) => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: unknown }).msg ?? '');
          }
          return '';
        })
        .filter(Boolean)
        .join('; ');
      if (text) return text;
    }
    if (typeof record.message === 'string' && record.message.trim()) return record.message;
  }
  return 'Failed to fetch NDT Analytics';
}

async function postNdt<T>(url: string, payload: unknown): Promise<NdtFetchResult<T>> {
  try {
    const res = await axiosConfig.post(url, payload ?? {});
    const body = unwrap(res);
    if (isNoRecords(body) || isNoRecords(res.data)) {
      return { data: null, noRecords: true, error: null };
    }
    if (res.status === 200 || res.status === 201) {
      return { data: (body as T) ?? null, noRecords: false, error: null };
    }
    return { data: null, noRecords: false, error: readNdtError(body) };
  } catch (error) {
    const message = readNdtError(error);
    if (message.includes('No records found')) {
      return { data: null, noRecords: true, error: null };
    }
    return { data: null, noRecords: false, error: message };
  }
}

function ndtUrl(path: string): string {
  return `${ndtApiBaseUrl}${path.replace(/^\//, '')}`;
}

export function fetchCombinedNdtSummary(payload: unknown) {
  return postNdt(ndtUrl('api/reports/summary/ai'), payload);
}

export function fetchCombinedNdtInsights(payload: unknown) {
  return postNdt(ndtUrl('api/reports/summary/ml'), payload);
}
